import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { SiteRecord } from '../api/sites';

// Extend jsPDF type to include autoTable
declare module 'jspdf' {
  interface jsPDF {
    autoTable: typeof autoTable;
    lastAutoTable?: {
      finalY: number;
    };
  }
}

interface PDFColors {
  primary: string;
  secondary: string;
  accent: string;
  text: string;
  textLight: string;
  background: string;
  border: string;
  headerBg: string;
  alternateRow: string;
}

const colors: PDFColors = {
  primary: '#1a73e8',
  secondary: '#34a853',
  accent: '#ea4335',
  text: '#202124',
  textLight: '#5f6368',
  background: '#ffffff',
  border: '#dadce0',
  headerBg: '#f1f3f4',
  alternateRow: '#f8f9fa'
};

// Convert hex color to RGB array
const hexToRgb = (hex: string): [number, number, number] => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
    : [0, 0, 0];
};

// Format date to readable string
const formatDate = (dateString: string | null): string => {
  if (!dateString) return 'Not specified';
  try {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch {
    return 'Invalid date';
  }
};

// Add header with logo and title
const addHeader = (doc: jsPDF, title: string) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Header background
  doc.setFillColor(...hexToRgb(colors.primary));
  doc.rect(0, 0, pageWidth, 40, 'F');
  
  // Title
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text('Pilbara Risk Solutions', 20, 20);
  
  doc.setFontSize(14);
  doc.setFont('helvetica', 'normal');
  doc.text(title, 20, 32);
};

// Add footer with page numbers
const addFooter = (doc: jsPDF, pageNumber: number, totalPages: number) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  doc.setFillColor(...hexToRgb(colors.headerBg));
  doc.rect(0, pageHeight - 15, pageWidth, 15, 'F');
  
  doc.setTextColor(...hexToRgb(colors.textLight));
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  
  // Left side - Generated date
  const generatedText = `Generated: ${new Date().toLocaleDateString('en-AU')} ${new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}`;
  doc.text(generatedText, 20, pageHeight - 7);
  
  // Right side - Page number
  const pageText = `Page ${pageNumber} of ${totalPages}`;
  const pageTextWidth = doc.getTextWidth(pageText);
  doc.text(pageText, pageWidth - pageTextWidth - 20, pageHeight - 7);
};

// Add section header
const addSectionHeader = (doc: jsPDF, title: string, yPosition: number): number => {
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Section background
  doc.setFillColor(...hexToRgb(colors.headerBg));
  doc.roundedRect(15, yPosition, pageWidth - 30, 12, 2, 2, 'F');
  
  // Section title
  doc.setTextColor(...hexToRgb(colors.primary));
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(title, 20, yPosition + 8);
  
  return yPosition + 18;
};

// Add info box
const addInfoBox = (doc: jsPDF, label: string, value: string, x: number, y: number, width: number) => {
  // Box background
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(...hexToRgb(colors.border));
  doc.setLineWidth(0.5);
  doc.roundedRect(x, y, width, 20, 2, 2, 'FD');
  
  // Label
  doc.setTextColor(...hexToRgb(colors.textLight));
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(label, x + 5, y + 8);
  
  // Value
  doc.setTextColor(...hexToRgb(colors.text));
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(value, x + 5, y + 16);
};

// Generate PDF for a single site
export const generateSitePDF = async (site: SiteRecord): Promise<void> => {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  let currentY = 50;

  // Add header
  addHeader(doc, 'Site Risk Assessment Report');

  // Site Information Section
  currentY = addSectionHeader(doc, 'Site Information', currentY);
  
  // Info boxes in a grid
  const boxWidth = (pageWidth - 50) / 2;
  addInfoBox(doc, 'Site Name', site.siteName || 'Untitled Site', 20, currentY, boxWidth);
  addInfoBox(doc, 'Location', site.location || 'Not specified', 20 + boxWidth + 10, currentY, boxWidth);
  
  currentY += 28;
  addInfoBox(doc, 'Status', site.status || 'Draft', 20, currentY, boxWidth);
  addInfoBox(doc, 'Created', formatDate(site.createdAt), 20 + boxWidth + 10, currentY, boxWidth);
  
  currentY += 28;

  // Hazard Summary
  currentY = addSectionHeader(doc, `Hazard Register (${site.hazardEntries?.length || 0} hazards)`, currentY);
  
  if (site.hazardEntries && site.hazardEntries.length > 0) {
    const hazardData = site.hazardEntries.map((hazard, index) => [
      `${index + 1}`,
      `${hazard.areaLevel || ''} ${hazard.roomLocation || ''}`.trim() || 'N/A',
      hazard.materialItem || 'N/A',
      hazard.hazardType || 'N/A',
      hazard.hazardStatus || 'N/A',
      hazard.riskStatus || 'N/A'
    ]);

    autoTable(doc, {
      startY: currentY,
      head: [['#', 'Location', 'Material/Item', 'Hazard Type', 'Status', 'Risk Status']],
      body: hazardData,
      theme: 'grid',
      headStyles: {
        fillColor: hexToRgb(colors.primary),
        textColor: [255, 255, 255],
        fontSize: 10,
        fontStyle: 'bold',
        halign: 'center'
      },
      bodyStyles: {
        fontSize: 9,
        textColor: hexToRgb(colors.text)
      },
      alternateRowStyles: {
        fillColor: hexToRgb(colors.alternateRow)
      },
      columnStyles: {
        0: { cellWidth: 10, halign: 'center' },
        1: { cellWidth: 35 },
        2: { cellWidth: 35 },
        3: { cellWidth: 25, halign: 'center' },
        4: { cellWidth: 40 },
        5: { cellWidth: 25, halign: 'center' }
      },
      margin: { left: 20, right: 20 },
      didDrawPage: (data) => {
        // Don't add footer here, we'll add it at the end
      }
    });

    currentY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : currentY + 50;
  } else {
    doc.setTextColor(...hexToRgb(colors.textLight));
    doc.setFontSize(11);
    doc.setFont('helvetica', 'italic');
    doc.text('No hazards recorded for this site.', 20, currentY);
    currentY += 15;
  }

  // Add footer to all pages
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    addFooter(doc, i, totalPages);
  }

  // Save the PDF
  const fileName = `${site.siteName || 'Site'}_Risk_Assessment_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
};

// Generate PDF for multiple sites (summary)
export const generateSitesSummaryPDF = async (sites: SiteRecord[]): Promise<void> => {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  let currentY = 50;

  // Add header
  addHeader(doc, 'Sites Summary Report');

  // Summary statistics
  currentY = addSectionHeader(doc, 'Overview', currentY);
  
  const totalHazards = sites.reduce((sum, site) => sum + (site.hazardEntries?.length || 0), 0);
  const avgHazards = sites.length > 0 ? (totalHazards / sites.length).toFixed(1) : '0';
  
  const boxWidth = (pageWidth - 50) / 3;
  addInfoBox(doc, 'Total Sites', sites.length.toString(), 20, currentY, boxWidth);
  addInfoBox(doc, 'Total Hazards', totalHazards.toString(), 20 + boxWidth + 5, currentY, boxWidth);
  addInfoBox(doc, 'Avg. Hazards/Site', avgHazards, 20 + (boxWidth + 5) * 2, currentY, boxWidth);
  
  currentY += 30;

  // Sites table
  currentY = addSectionHeader(doc, 'Sites List', currentY);
  
  const sitesData = sites.map(site => [
    site.siteName || 'Untitled',
    site.location || 'Not specified',
    site.hazardEntries?.length.toString() || '0',
    site.status || 'Draft',
    formatDate(site.createdAt)
  ]);

  autoTable(doc, {
    startY: currentY,
    head: [['Site Name', 'Location', 'Hazards', 'Status', 'Created']],
    body: sitesData,
    theme: 'grid',
    headStyles: {
      fillColor: hexToRgb(colors.primary),
      textColor: [255, 255, 255],
      fontSize: 10,
      fontStyle: 'bold'
    },
    bodyStyles: {
      fontSize: 9
    },
    alternateRowStyles: {
      fillColor: hexToRgb(colors.alternateRow)
    },
    columnStyles: {
      0: { cellWidth: 50 },
      1: { cellWidth: 45 },
      2: { cellWidth: 20, halign: 'center' },
      3: { cellWidth: 25, halign: 'center' },
      4: { cellWidth: 30 }
    },
    margin: { left: 20, right: 20 }
  });

  // Add footer
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    addFooter(doc, i, totalPages);
  }

  // Save the PDF
  const fileName = `Sites_Summary_Report_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
};
